import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  TemplateRef,
  ViewChild,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { LoadingService } from '@core/services/loading.service';
import { MutualFundService } from '@core/services/mutual-fund.service';
import { Store } from '@ngxs/store';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { TabsComponent } from '@shared/ui/tabs/tabs.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-benchmark-import',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SlideInRightModalComponent,
    InputLabelComponent,
    ButtonDirective,
    TabsComponent,
  ],
  templateUrl: './benchmark-import.component.html',
  styleUrl: './benchmark-import.component.scss'
})
export class BenchmarkImportComponent implements OnInit, AfterViewInit, OnDestroy {
  private fb = inject(FormBuilder);
  private notify = inject(NotificationFacade);
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private change = inject(ChangeDetectorRef);
  private mutualFundService = inject(MutualFundService);
  private store = inject(Store);

  @ViewChild('dataFileTemplate')
  dataFileTemplate!: TemplateRef<any>;
  @ViewChild('manualUploadTemplate')
  manualUploadTemplate!: TemplateRef<any>;

  mode: 'file' | 'manual' = 'file';
  selectedTab = 0;
  selectedFileName = '';
  isLoading$!: Observable<boolean>;
  subs: Subscription[] = [];
  tabs: { label: string; contentTemplate: TemplateRef<any> }[] = [];

  
  benchmarkImportForm = this.fb.nonNullable.group({
    file: [null as File | null],
    date: ['', Validators.required],
    rate: [null as number | null, [Validators.required, Validators.min(0)]],
  });

  constructor() {
    this.setMode('file');
    this.isLoading$ = this.loadingService.getLoadingObservable('upload-benchmark-file');
  }

  ngOnInit(): void {
    this.setMode('file');
  }

  ngAfterViewInit(): void {
    this.tabs = [
      { label: 'Data file upload', contentTemplate: this.dataFileTemplate },
      { label: 'Daily benchmark input', contentTemplate: this.manualUploadTemplate },
    ];
    this.change.detectChanges();
  }



  setMode(mode: 'file' | 'manual') {
    this.mode = mode;

    if (mode === 'file') {
      this.benchmarkImportForm.get('date')?.disable();
      this.benchmarkImportForm.get('rate')?.disable();
      this.benchmarkImportForm.patchValue({ date: '', rate: null });
      return;
    }

    this.selectedFileName = '';
    this.benchmarkImportForm.patchValue({ file: null });
    this.benchmarkImportForm.get('date')?.enable();
    this.benchmarkImportForm.get('rate')?.enable();
  }

  onTabChange(index: number) {
    this.selectedTab = index;
    this.setMode(index === 0 ? 'file' : 'manual');
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.benchmarkImportForm.patchValue({ file });
    this.selectedFileName = file?.name || '';
  }

  saveBenchmark() {
    if (this.mode === 'file') {
      const file = this.benchmarkImportForm.get('file')?.value;
      if (!file) {
        this.notify.error('Please upload a benchmark data file.');
        return;
      }

      const sub = this.mutualFundService.uploadBenchmarkFile(file).subscribe({
        next: (response) => {
          this.notify.success('Benchmark file uploaded successfully.');
          this.closeModal();
        },
        error: (error) => {
          this.notify.error('Failed to upload benchmark file. Please try again.');
        }
      });
      this.subs.push(sub);
      return;
    }

    const dateControl = this.benchmarkImportForm.get('date');
    const rateControl = this.benchmarkImportForm.get('rate');
    dateControl?.markAsTouched();
    rateControl?.markAsTouched();

    if (dateControl?.invalid || rateControl?.invalid) {
      this.notify.error('Please provide a valid date and rate.');
      return;
    }

    const sub = this.mutualFundService.saveBenchmarkData({
      date: this.benchmarkImportForm.getRawValue().date,
      rate: this.benchmarkImportForm.getRawValue().rate?.toString() || '0',
    }).subscribe({
      next: (response) => {
        this.notify.success('Benchmark data added successfully.');
         this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'benchmark',
            params: new Pagination({ 
              skip: 0, 
              size: 10 
            }),
          }));
        this.closeModal();
      },
      error: (error) => {
        this.notify.error('Failed to add benchmark data. Please try again.');
      }
    });
    this.subs.push(sub);
  }

  closeModal() {
    this.router.navigateByUrl('/admin/transactions/mutual-funds/benchmark');
  }

    ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

}
